/**
 * StepStorySelection Component
 *
 * Combined step 2 of the story creation wizard.
 * Shows the premade concept grid by default. A secondary CTA below the grid
 * swaps the view to the inline Pixie chat (StepPlotRefinement) for AI plot creation.
 */

import { useEffect, useState } from 'react'

import { faBook } from '@fortawesome/pro-light-svg-icons/faBook'
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles'
import { Alert, Box, CircularProgress, Grid2 as Grid, styled, Typography } from '@mui/material'

import { useSessionsPremadeConceptsList } from '@/shared/api/generated/story-sessions/story-sessions'
import { PlotOutline, PremadeStoryConceptDetail } from '@/shared/api/models'
import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'

import { storyPlotSuggestions } from '../constants/storyPlotSuggestions'
import { StepPlotRefinement } from './StepPlotRefinement'

interface StepStorySelectionProps {
  sessionUuid: string
  characterCount: number
  languageCode: string
  onSelectPremade: (concept: PremadeStoryConceptDetail) => void
  onPlotAccepted: (plot: PlotOutline) => void
  onAIModeEntered: () => void
}

type Mode = 'premade' | 'ai'

export const StepStorySelection: React.FC<StepStorySelectionProps> = ({
  sessionUuid,
  characterCount,
  languageCode,
  onSelectPremade,
  onPlotAccepted,
  onAIModeEntered,
}) => {
  const [mode, setMode] = useState<Mode>('premade')
  const [autoEnteredAI, setAutoEnteredAI] = useState(false)

  const {
    data: concepts,
    isLoading: isLoadingConcepts,
    error: conceptsError,
  } = useSessionsPremadeConceptsList(
    { num_characters: characterCount, language_code: languageCode },
    { query: { enabled: mode === 'premade' } },
  )

  const hasAvailableConcepts = !!concepts?.some((c) => c.matched_variant_id !== null)

  // Auto-fall-back to AI mode when no premade stories are available for these characters
  useEffect(() => {
    if (mode !== 'premade' || isLoadingConcepts || conceptsError || !concepts) return
    if (hasAvailableConcepts) return

    onAIModeEntered()
    setAutoEnteredAI(true)
    setMode('ai')
  }, [mode, isLoadingConcepts, conceptsError, concepts, hasAvailableConcepts, onAIModeEntered])

  const handleConceptSelect = (concept: PremadeStoryConceptDetail) => {
    if (concept.matched_variant_id !== null) {
      onSelectPremade(concept)
    }
  }

  const handleEnterAIMode = () => {
    onAIModeEntered()
    setAutoEnteredAI(false)
    setMode('ai')
  }

  if (mode === 'ai') {
    return (
      <StyledContainer>
        {!autoEnteredAI && (
          <Box sx={{ mb: 2 }}>
            <Button onClick={() => setMode('premade')}>
              ← Back to premade stories
            </Button>
          </Box>
        )}
        <StepPlotRefinement
          sessionUuid={sessionUuid}
          onContinue={onPlotAccepted}
          suggestions={storyPlotSuggestions}
        />
      </StyledContainer>
    )
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h5" component="h2">
          Choose Your Story
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick an adventure your child will star in.
        </Typography>
      </StyledHeader>

      {isLoadingConcepts && (
        <StyledLoadingBox>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading stories...
          </Typography>
        </StyledLoadingBox>
      )}

      {!!conceptsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load premade stories. Please try again.
        </Alert>
      )}

      {concepts && hasAvailableConcepts && (
        <Grid container spacing={3}>
          {concepts.map((concept) => {
            const isAvailable = concept.matched_variant_id !== null

            return (
              <Grid key={concept.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <StyledConceptCard
                  $disabled={!isAvailable}
                  onClick={() => isAvailable && handleConceptSelect(concept)}
                >
                  {concept.thumbnail_url ? (
                    <StyledThumbnail
                      src={concept.thumbnail_url}
                      alt={concept.title}
                    />
                  ) : (
                    <StyledThumbnailPlaceholder>
                      <Icon icon={faBook} />
                    </StyledThumbnailPlaceholder>
                  )}
                  <StyledConceptBody>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {concept.title}
                    </Typography>
                    {concept.description && (
                      <Typography variant="body2" color="text.secondary">
                        {concept.description}
                      </Typography>
                    )}
                    {!isAvailable && (
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                        Not available for your characters
                      </Typography>
                    )}
                  </StyledConceptBody>
                </StyledConceptCard>
              </Grid>
            )
          })}
        </Grid>
      )}

      <StyledAICta>
        <StyledDivider>
          <span>or</span>
        </StyledDivider>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Want something unique?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Chat with Pixie to create your own one-of-a-kind story.
        </Typography>
        <Button onClick={handleEnterAIMode}>
          <Icon icon={faWandMagicSparkles} style={{ marginRight: pxToRem(8) }} />
          Chat with Pixie
        </Button>
      </StyledAICta>
    </StyledContainer>
  )
}

// --- Styled components ---

const StyledContainer = styled(Box)`
  max-width: ${pxToRem(900)};
  margin: 0 auto;
`

const StyledHeader = styled(Box)`
  text-align: center;
  margin-bottom: ${pxToRem(32)};
`

const StyledLoadingBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${pxToRem(48)};
`

const StyledConceptCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$disabled',
})<{ $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${(props) => props.theme.palette.background.paper};
  border: 2px solid ${(props) => props.theme.palette.divider};
  border-radius: ${pxToRem(12)};
  overflow: hidden;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  ${(props) =>
    !props.$disabled &&
    `
    &:hover {
      border-color: ${props.theme.palette.primary.main};
      box-shadow: 0 4px 16px ${props.theme.palette.primary.main}20;
    }
  `}
`

const StyledThumbnail = styled('img')`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
`

const StyledThumbnailPlaceholder = styled(Box)`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.palette.grey[100]};
  font-size: ${pxToRem(48)};
  color: ${(props) => props.theme.palette.text.disabled};
`

const StyledConceptBody = styled(Box)`
  padding: ${pxToRem(16)};
  flex: 1;
`

const StyledAICta = styled(Box)`
  margin-top: ${pxToRem(48)};
  text-align: center;
`

const StyledDivider = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(16)};
  margin-bottom: ${pxToRem(24)};
  color: ${(props) => props.theme.palette.text.secondary};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${(props) => props.theme.palette.divider};
  }

  span {
    font-size: ${pxToRem(14)};
    text-transform: uppercase;
    letter-spacing: ${pxToRem(1)};
  }
`

export default StepStorySelection
